"use client";

import type { WhopElement } from "@whop/embedded-components-vanilla-js/types";
import React, { useEffect, useLayoutEffect, useMemo, useRef } from "react";
import { useElementSnapshot } from "./use-element-snapshot";

/**
 * Base element type that all created elements conform to.
 * Using WhopElement<any, any, any> gives us access to common methods
 * (mount, unmount, on, off, updateOptions) without union type issues.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type BaseElement = WhopElement<any, any, any>;

/**
 * The minimal session surface the wrapper needs — the registry-typed
 * createElement every session exposes.
 */
interface SessionLike<Registry extends Record<string, [unknown, unknown]>> {
	createElement<T extends keyof Registry>(
		type: T | { type: T },
		options: Registry[T][0],
	): unknown;
}

interface WithFallbackPropsBase {
	className?: string;
	style?: React.CSSProperties;
	onReady?: () => void;
	fallback?: React.ReactNode;
}

export interface WithFallbackPropsRequired<
	TOptions,
> extends WithFallbackPropsBase {
	options: TOptions;
}

export interface WithFallbackPropsOptional<
	TOptions,
> extends WithFallbackPropsBase {
	options?: TOptions;
}

type WithFallbackComponent<TOptions, TRequired extends boolean> = {
	(
		props: TRequired extends true
			? WithFallbackPropsRequired<TOptions>
			: WithFallbackPropsOptional<TOptions>,
	): React.ReactNode;
	displayName: string;
	type: string;
};

/**
 * Builds a namespace-specific `withFallback` from that namespace's session
 * hook. The returned helper creates a React component that wraps an embedded
 * element with fallback support.
 *
 * @example
 * ```tsx
 * // src/wallet/with-fallback.tsx
 * export const withFallback = createWithFallback<WalletSessionElements>(useWalletSession);
 *
 * // src/wallet/elements/deposit.tsx — options type inferred from the element type
 * export const DepositElement = withFallback({
 *   displayName: "DepositElement",
 *   type: "deposit-element",
 * } as const);
 * ```
 */
export function createWithFallback<
	Registry extends Record<string, [unknown, unknown]>,
>(useSession: () => SessionLike<Registry> | null) {
	return function withFallback<
		const T extends keyof Registry & string,
		TRequired extends boolean = false,
	>(Component: {
		displayName: string;
		type: T;
	}): WithFallbackComponent<Registry[T][0], TRequired> {
		type TOptions = Registry[T][0];

		function WrappedElement({
			options,
			className,
			style,
			onReady,
			fallback,
		}: WithFallbackPropsOptional<TOptions>) {
			const session = useSession();
			const ref = useRef<HTMLDivElement>(null);

			const element = useMemo((): BaseElement | null => {
				if (!session) return null;
				// Safe: T is constrained to valid element types, and options matches T
				return session.createElement(
					Component,
					options ?? ({} as TOptions),
				) as BaseElement;
				// eslint-disable-next-line react-hooks/exhaustive-deps -- element is created once per session; options updates flow through updateOptions below
			}, [session]);

			const elementSnapshot = useElementSnapshot(element);

			const isReady = elementSnapshot?.state === "ready";

			useEffect(() => {
				if (!element) return;
				element.updateOptions(options ?? {});
			}, [options, element]);

			useEffect(() => {
				if (!element || !onReady) return;
				element.on("ready", onReady);
				return () => {
					element.off("ready", onReady);
				};
			}, [element, onReady]);

			useLayoutEffect(() => {
				if (!element || !ref.current) return;
				element.mount(ref.current);
				return () => {
					element.unmount();
				};
				// eslint-disable-next-line react-hooks/exhaustive-deps -- ref.current intentionally re-runs the mount when the container attaches
			}, [element, ref.current]);

			return (
				<>
					<div
						style={{ ...style, visibility: isReady ? undefined : "hidden" }}
						className={className}
						ref={ref}
					/>
					{!isReady && (fallback ?? null)}
				</>
			);
		}

		WrappedElement.displayName = Component.displayName;
		WrappedElement.type = Component.type;

		return WrappedElement as WithFallbackComponent<TOptions, TRequired>;
	};
}
